import { ObjectPool } from './ObjectPool.js';
import { ObjectFactory } from './ObjectFactory.js';
import { AssetLoader } from './AssetLoader.js';

/**
 * Central registry for object pools with biome-aware operations.
 * Manages pool lifecycle, warm-up, and biome transitions.
 */
export class PoolManager {
    constructor(preloader = null) {
        this.assetLoader = new AssetLoader();
        this.factory = new ObjectFactory(this.assetLoader);

        // Pool registry (typeId -> ObjectPool)
        this.pools = new Map();

        // Configuration
        this.config = null;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('poolManager', {
            dispose: () => this.dispose()
        });
    }

    /**
     * Initialize the pool manager with configuration.
     * @param {string} configPath - Path to roadside-objects.json
     */
    async initialize(configPath) {
        // Load configuration
        const response = await fetch(configPath);
        this.config = await response.json();

        // Initialize factory with config
        await this.factory.initialize(this.config);

        // Create pools for each type
        for (const [typeId, typeConfig] of Object.entries(this.config.types)) {
            const pool = new ObjectPool({
                typeId,
                factory: () => this.factory.create(typeId, {}),
                reset: (obj, options) => this.factory.reset(obj, options),
                maxSize: typeConfig.poolSize || 100
            });

            this.pools.set(typeId, pool);
        }

        this._onPreloadComplete?.();
    }

    /**
     * Acquire an object from the appropriate pool.
     * @param {string} typeId - Type identifier
     * @param {Object} [options] - Options (color, etc.)
     * @returns {THREE.Object3D|null}
     */
    acquire(typeId, options = {}) {
        const pool = this.pools.get(typeId);
        if (!pool) {
            console.warn(`PoolManager: Unknown type "${typeId}"`);
            return null;
        }

        return pool.acquire(options);
    }

    /**
     * Release an object back to its pool.
     * @param {THREE.Object3D} obj - Object to release
     */
    release(obj) {
        const typeId = obj.userData?._poolTypeId;
        if (!typeId) {
            console.warn('PoolManager: Object has no pool type');
            return;
        }

        const pool = this.pools.get(typeId);
        if (pool) {
            pool.release(obj);
        }
    }

    /**
     * Warm up pools for a specific biome.
     * @param {string} biomeName - Biome name (e.g., "Desert")
     */
    warmUpForBiome(biomeName) {
        const biomeConfig = this.config?.biomes?.[biomeName];
        if (!biomeConfig) {
            console.warn(`PoolManager: Unknown biome "${biomeName}"`);
            return;
        }

        const warmUpCount = biomeConfig.warmUpCount || 20;
        const objectTypes = biomeConfig.objectTypes || [];

        for (const typeId of objectTypes) {
            const pool = this.pools.get(typeId);
            if (pool) {
                // Warm up proportional to weight
                const weight = biomeConfig.weights?.[typeId] || 0.25;
                const count = Math.ceil(warmUpCount * weight);
                pool.warmUp(count);
            }
        }
    }

    /**
     * Release all objects for a specific biome.
     * @param {string} biomeName - Biome name
     */
    releaseAllForBiome(biomeName) {
        const biomeConfig = this.config?.biomes?.[biomeName];
        if (!biomeConfig) return;

        const objectTypes = biomeConfig.objectTypes || [];
        for (const typeId of objectTypes) {
            const pool = this.pools.get(typeId);
            if (pool) {
                pool.releaseAll();
            }
        }
    }

    /**
     * Get object types available for a biome.
     * @param {string} biomeName - Biome name
     * @returns {string[]}
     */
    getTypesForBiome(biomeName) {
        const biomeConfig = this.config?.biomes?.[biomeName];
        return biomeConfig?.objectTypes || [];
    }

    /**
     * Get spawn weight for a type in a biome.
     * @param {string} biomeName - Biome name
     * @param {string} typeId - Type identifier
     * @returns {number}
     */
    getSpawnWeight(biomeName, typeId) {
        const biomeConfig = this.config?.biomes?.[biomeName];
        return biomeConfig?.weights?.[typeId] || 0;
    }

    /**
     * Get all weights for a biome (for weighted random selection).
     * @param {string} biomeName - Biome name
     * @returns {Object} - { typeId: weight }
     */
    getWeightsForBiome(biomeName) {
        const biomeConfig = this.config?.biomes?.[biomeName];
        return biomeConfig?.weights || {};
    }

    /**
     * Perform weighted random selection of a type for a biome.
     * @param {string} biomeName - Biome name
     * @returns {string|null} - Selected type ID
     */
    selectTypeForBiome(biomeName) {
        const types = this.getTypesForBiome(biomeName);
        const weights = this.getWeightsForBiome(biomeName);

        if (types.length === 0) return null;

        // Calculate total weight
        let totalWeight = 0;
        for (const typeId of types) {
            totalWeight += weights[typeId] || 0.1;
        }

        // Select randomly based on weight
        let random = Math.random() * totalWeight;
        for (const typeId of types) {
            const weight = weights[typeId] || 0.1;
            if (random < weight) {
                return typeId;
            }
            random -= weight;
        }

        // Fallback to last type
        return types[types.length - 1];
    }

    /**
     * Update all rock materials to a new color.
     * Called by BiomeRenderer during day/night color transitions.
     * @param {number} color - Color as hex number
     */
    setRockColor(color) {
        this.factory.setRockColor(color);
    }

    /**
     * Get debug statistics for all pools.
     * @returns {Object}
     */
    getStats() {
        const stats = {};
        for (const [typeId, pool] of this.pools) {
            stats[typeId] = {
                active: pool.getActiveCount(),
                free: pool.getFreeCount(),
                total: pool.getTotalCreated(),
                maxSize: pool.maxSize
            };
        }
        return stats;
    }

    /**
     * Dispose all pools and resources.
     */
    dispose() {
        // Guard against double-dispose
        if (this._disposed) return;
        this._disposed = true;

        for (const pool of this.pools.values()) {
            pool.dispose();
        }
        this.pools.clear();

        this.factory?.dispose();
        this.assetLoader?.dispose();
    }
}
